import type { Command } from "@/commands/Command";
import {
  MoveNodesInTreeCommand,
  UpdateNodeBoundsCommand,
  UpdateNodeTransformCommand,
  UpdateTextCommand,
} from "@/commands/nodeCommands";
import { getAllSceneNodes, resolveActivePageId } from "@/document/model/document";
import type { SceneNode } from "@/document/model/nodes";
import { editableNodeIds, isNodeLockedForEditing } from "@/document/operations/nodeLocking";
import { frameDropTargetForMovedNodes } from "@/editor/shell/canvasDropTargets";
import { transformBoundsInGroup } from "@/editor/tools/hitTest";
import { boundsEqual, transformEqual } from "@/editor/shell/shellSelection";
import { documentStore } from "@/editor/store";
import type { Rect } from "@/lib/math/rect";
import type { EditorCommandHost } from "../editorCommandHost";

export function updateNodeBounds(
  host: EditorCommandHost,
  updates: Array<{ nodeId: string; bounds: Rect }>,
  label = "Updated bounds",
  options: { dropPoint?: { x: number; y: number }; ignoreChildConstraints?: boolean } = {},
): void {
  const document = documentStore.getDocument();
  const resolvedActivePageId = resolveActivePageId(document, host.currentSelection().activePageId);
  const nodes = getAllSceneNodes(document);
  const commands: Command[] = updates.flatMap(update => {
    const node = nodes.find(candidate => candidate.id === update.nodeId);
    if (!node || isNodeLockedForEditing(document, node.id) || boundsEqual(node.bounds, update.bounds)) {
      return [];
    }

    const boundsCommand = new UpdateNodeBoundsCommand(update.nodeId, node.bounds, update.bounds, {
      ignoreChildConstraints: options.ignoreChildConstraints,
    });
    if (node.type !== "text" || node.text.textLayout === "FIXED_SIZE") {
      return [boundsCommand];
    }

    return [
      boundsCommand,
      new UpdateTextCommand(update.nodeId, node.text, { ...node.text, textLayout: "FIXED_SIZE" }),
    ];
  });
  const movableNodeIds = editableNodeIds(document, updates.map(update => update.nodeId));
  const frameDropTarget = options.dropPoint
    ? frameDropTargetForMovedNodes(document, movableNodeIds, options.dropPoint, resolvedActivePageId)
    : undefined;
  if (frameDropTarget) {
    commands.push(new MoveNodesInTreeCommand(movableNodeIds, frameDropTarget));
  }

  host.runDocumentCommands(commands, label);
}

export function updateNodeTransforms(
  host: EditorCommandHost,
  updates: Array<{ nodeId: string; transform: SceneNode["transform"] }>,
  label = "Updated transform",
): void {
  const document = documentStore.getDocument();
  const nodes = getAllSceneNodes(document);
  const commands = updates.flatMap(update => {
    const node = nodes.find(candidate => candidate.id === update.nodeId);
    if (!node || isNodeLockedForEditing(document, node.id) || transformEqual(node.transform, update.transform)) {
      return [];
    }

    return [new UpdateNodeTransformCommand(update.nodeId, node.transform, update.transform)];
  });

  host.runDocumentCommands(commands, label);
}

export function updateSelectionBounds(
  host: EditorCommandHost,
  selectionNodeIds: string[],
  selectionBounds: Rect | undefined,
  toBounds: Rect,
): void {
  if (!selectionBounds) {
    return;
  }

  const document = documentStore.getDocument();
  const nodes = getAllSceneNodes(document).filter(
    node => selectionNodeIds.includes(node.id) && !isNodeLockedForEditing(document, node.id),
  );
  updateNodeBounds(
    host,
    nodes.map(node => ({
      nodeId: node.id,
      bounds: transformBoundsInGroup(node.bounds, selectionBounds, toBounds),
    })),
    "Updated properties",
  );
}
